import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { arrayMove } from '@dnd-kit/sortable'

import { Alert, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import Filters from '@/configuration/components/Filters/Filters'

import AddDeliveryMethodButton from './AddDeliveryMethodButton/AddDeliveryMethodButton'
import DeleteDeliveryMethodModal from './DeleteDeliveryMethodModal/DeleteDeliveryMethodModal'
import DeliveryMethodsConfigurationSkeleton from './DeliveryMethodsConfigurationSkeleton/DeliveryMethodsConfigurationSkeleton'
import useDeliveryMethods from './hooks/useDeliveryMethods/useDeliveryMethods'
import useFilter from './hooks/useFilter/useFilter'
import SortableDeliveryMethodList from './SortableDeliveryMethodList/SortableDeliveryMethodList'
import * as Styles from './styles'

const DeliveryMethodsTab = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const {
    regions,
    carriers,
    fetchingCarriers,
    deliveryMethods,
    setDeliveryMethods,
    editingDeliveryMethods,
    gettingRegions,
    gettingLogisticsPreferences,
    hasChanges,
    revertChanges,
    deliveryMethodSelected,
    showDeleteDeliveryMethodModal,
    setShowDeleteDeliveryMethodModal,
    onDeleteDeliveryMethod,
    onEditDeliveryMethod,
    onSave,
    onDuplicateDeliveryMethod,
    onCreate,
    handleChangeEnabled,
    handleOnOpenDeleteDeliveryMethodModal,
  } = useDeliveryMethods({
    storeId: info?.selectedStore?.id,
    setToastInfo,
  })

  const { filteredDeliveryMethods, filters, setFilters } = useFilter(deliveryMethods)

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: filteredDeliveryMethods,
    initialElementsToShow: 10,
  })

  const [dragging, setDragging] = useState(false)
  const { hasPermission } = useUserPermissions()

  const onSortEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = deliveryMethods.findIndex(r => r.id === active.id)
    const newIndex = deliveryMethods.findIndex(r => r.id === over.id)

    setDeliveryMethods(deliveryMethods => [...arrayMove(deliveryMethods, oldIndex, newIndex)])
    setDragging(false)
    document.body.style.cursor = 'inherit'
  }

  const onSortStart = () => {
    setDragging(true)
    document.body.style.cursor = 'grabbing'
  }

  const handleShouldCancelStart = e => {
    return e?.target?.id !== 'draggable-icon'
  }

  const handleCloseDeleteDeliveryMethodModal = () => setShowDeleteDeliveryMethodModal(false)

  if (gettingRegions || gettingLogisticsPreferences || fetchingCarriers) {
    return <DeliveryMethodsConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editLogistics)} />}
      <Filters
        source="deliveryMethods"
        regions={regions}
        carriers={carriers}
        filters={filters}
        setFilters={setFilters}
        queryFilterPlaceholder={t('Nombre de método, Nombre personalizado')}
      />
      <SortableDeliveryMethodList
        deliveryMethods={elementsToShow || []}
        regions={regions}
        shouldCancelStart={handleShouldCancelStart}
        handleChangeEnabled={handleChangeEnabled}
        handleOnEdit={onEditDeliveryMethod}
        handleOnOpenDeleteDeliveryMethodModal={handleOnOpenDeleteDeliveryMethodModal}
        handleDuplicateDeliveryMethod={onDuplicateDeliveryMethod}
        onSortEnd={onSortEnd}
        onSortStart={onSortStart}
        dragging={dragging}
        isEditable={hasPermission(userPermissions.editLogistics)}
      />
      {elementsToShow.length > 0 && (
        <Alert type="info">
          {t(
            'Para evitar problemas de re-importación y aduanas, los métodos de devolución disponibles en el portal para devoluciones internacionales se limitarán, siempre que sea posible, a aquellos que utilicen el mismo transportista que el envío original.'
          )}
        </Alert>
      )}
      <LoadMore
        numberOfElementsToShow={numberOfElementsToShow}
        totalElements={totalElements}
        hasMoreElements={hasMoreElements}
        showMore={showMore}
      />
      <Styles.ActionsContainer>
        {hasPermission(userPermissions.editLogistics) && <AddDeliveryMethodButton onAdd={onCreate} />}
      </Styles.ActionsContainer>
      <DeleteDeliveryMethodModal
        deliveryMethod={deliveryMethodSelected}
        show={showDeleteDeliveryMethodModal}
        onClose={handleCloseDeleteDeliveryMethodModal}
        onDelete={onDeleteDeliveryMethod}
      />
      <SaveChangesButtons
        hasChanges={hasPermission(userPermissions.editLogistics) && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={editingDeliveryMethods}
        disabled={editingDeliveryMethods}
      />
    </Styles.Container>
  )
}

export default DeliveryMethodsTab
